/**
 * PartidaModal
 * Modal para crear/editar partidas
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';
import { partidaService, areaService } from '../services/catalogoService';
import './PartidaModal.css';

const PartidaModal = ({ isOpen, mode = 'create', partida = null, areaId = null, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    unidad: '',
    area: areaId || ''
  });
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar áreas cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      cargarAreas();
    }
  }, [isOpen]);

  useEffect(() => {
    if (mode === 'edit' && partida) {
      setFormData({
        nombre: partida.nombre || '',
        descripcion: partida.descripcion || '',
        unidad: partida.unidad || '',
        area: partida.area || ''
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        unidad: '',
        area: areaId || ''
      });
    }
  }, [mode, partida, areaId, isOpen]);

  const cargarAreas = async () => {
    try {
      const response = await areaService.getAll({ status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setAreas(data);
    } catch (error) {
      console.error('Error al cargar áreas:', error);
      toast.error('Error al cargar áreas');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre de la partida es requerido');
      return;
    }

    if (!formData.area) {
      toast.error('Debes seleccionar un área');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'create') {
        await partidaService.create({
          ...formData,
          status: 'activo'
        });
        toast.success('Partida creada exitosamente');
      } else if (mode === 'edit') {
        await partidaService.update(partida.id, formData);
        toast.success('Partida actualizada exitosamente');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al guardar partida:', error);
      toast.error('Error al guardar la partida');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="partida-modal-overlay">
      <div className="partida-modal">
        <div className="partida-modal-header">
          <h3>{mode === 'create' ? 'Crear Partida' : 'Editar Partida'}</h3>
          <button onClick={onClose} className="modal-close-btn">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="area">Área *</label>
            <select
              id="area"
              name="area"
              value={formData.area}
              onChange={handleChange}
              disabled={mode === 'edit' || (areaId && mode === 'create')}
            >
              <option value="">Selecciona un área</option>
              {areas.map(area => (
                <option key={area.id} value={area.id}>
                  {area.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="nombre">Nombre *</label>
            <input
              id="nombre"
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="Nombre de la partida"
            />
          </div>

          <div className="form-group">
            <label htmlFor="descripcion">Descripción</label>
            <textarea
              id="descripcion"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Descripción (opcional)"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="unidad">Unidad de Medida</label>
            <input
              id="unidad"
              type="text"
              name="unidad"
              value={formData.unidad}
              onChange={handleChange}
              placeholder="ej: m2, horas, unidades"
            />
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Guardando...' : mode === 'create' ? 'Crear' : 'Actualizar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartidaModal;
